// Die Zielwahl einer **Lenkwaffe** — Gegner vor Brocken, wie beim Laser.
//
// Eine Rakete sucht sich ihr Ziel im Flug. Bis hierher suchte sie es
// ausschließlich im Brockenfeld: sie flog an einem Gegner vorbei, verpuffte nach
// ihrer Laufzeit und traf ihn nur, wenn er zufällig im Splash-Radius eines
// Brocken-Einschlags stand.
//
// **Der Server bucht den Raketenanteil dagegen voll gegen Gegner** — er steckt
// in `area_dps`, und `_aim_hostiles` legt ihn auf. Solo war ein Gegner damit
// zäher als online, ohne dass irgendwo etwas rot wurde: genau der Fehlermodus,
// gegen den es „dieselben Testfälle auf beiden Seiten" gibt.
//
// Diese Datei importiert **kein Phaser** (Gate FG-18) und kennt weder Welt noch
// Gegnerfeld — sie bekommt zwei Listen und entscheidet.

/**
 * Das Wenige, das eine Lenkwaffe von ihrem Ziel braucht.
 *
 * **Bewusst schmal.** Ein Brocken trägt ein Erz, ein Gegner nicht; die
 * Drohnenordnung liest genau dieses Erz (`targeting.ts`, `byDroneOrder`). Ein
 * gemeinsames Interface, das es mitführte, zwänge jeden Gegner zu einem
 * erfundenen Erz — und die Neigung des Spielers würde still auf Gegner wirken.
 *
 * Vier Felder, und alle vier braucht die Lenkung wirklich: die ID für die
 * Sperre, `x`/`y` für den Kurs, `r` für den Einschlagabstand.
 *
 * Kein Feld sagt, **was** das Ziel ist. Das ist der Punkt: eine Unterscheidung
 * über das ID-Präfix (`e:`) wäre eine stille Typunterscheidung, die beim ersten
 * neuen ID-Raum bricht — und den gab es mit dem Wächter (`g:`) gerade erst.
 */
export interface GuidedTarget {
  id: string;
  x: number;
  y: number;
  r: number;
}

/** Das nächste Ziel einer Liste innerhalb des Radius — oder `null`. */
function nearest<T extends GuidedTarget>(
  x: number, y: number, radius: number, list: readonly T[],
): T | null {
  let best: T | null = null;
  let bestDist = radius * radius;
  for (const target of list) {
    const dist = (target.x - x) ** 2 + (target.y - y) ** 2;
    if (dist <= bestDist) { bestDist = dist; best = target; }
  }
  return best;
}

/**
 * Das Ziel einer Lenkwaffe — **Gegner zuerst, sonst der nächste Brocken**.
 *
 * Dieselbe Rangfolge, die der Laser schon hat, und dieselbe, die der Server für
 * den Flächenkanal rechnet: `_aim_hostiles` legt `area_dps` auf jeden Gegner in
 * Reichweite, **bevor** die Brocken drankommen.
 *
 * Sie allokiert nichts: beide Listen sind Puffer des Aufrufers, und
 * zurückgegeben wird ein Element daraus (Gate FG-14).
 */
export function pickGuidedTarget(
  x: number,
  y: number,
  radius: number,
  hostiles: readonly GuidedTarget[],
  rocks: readonly GuidedTarget[],
): GuidedTarget | null {
  return nearest(x, y, radius, hostiles) ?? nearest(x, y, radius, rocks);
}
